import axios from 'axios';
import { handleAuthExpiry } from './authExpiry';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Send trusted device token so server can skip 2FA on login
    const trustedDevice = localStorage.getItem('trustedDeviceToken');
    if (trustedDevice) {
      config.headers['X-Trusted-Device'] = trustedDevice;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration.
// Delegates to the shared handler so this instance and the global interceptor
// in index.js cannot drift (lesson 34). The previous inline version redirected
// on ANY 401 — including a wrong password on the login form — and defaulted an
// admin to /client/login when the stored user failed to parse.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    try { handleAuthExpiry(error); } catch (e) { /* never mask the original error */ }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Portfolio API
export const portfolioAPI = {
  getAll: (params) => api.get('/portfolio', { params }),
  getById: (id) => api.get(`/portfolio/${id}`),
  create: (formData) => api.post('/portfolio', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, formData) => api.put(`/portfolio/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/portfolio/${id}`),
  deleteImage: (id, filename) => api.delete(`/portfolio/${id}/images/${filename}`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
};

// Appointments API
// Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  getByNumber: (invoiceNumber) => api.get(`/invoices/number/${invoiceNumber}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  send: (id) => api.post(`/invoices/${id}/send`),
  createPaymentIntent: (id) => api.post(`/invoices/${id}/create-payment-intent`),
  markPaid: (id, data) => api.post(`/invoices/${id}/mark-paid`, data),
};

// Galleries API
export const galleriesAPI = {
  getAll: () => api.get('/galleries'),
  getById: (id) => api.get(`/galleries/${id}`),
  getByLink: (uniqueLink) => api.get(`/galleries/link/${uniqueLink}`),
  create: (formData) => api.post('/galleries', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  addFiles: (id, formData) => api.post(`/galleries/${id}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  addVirtualTour: (id, data) => api.post(`/galleries/${id}/virtual-tour`, data),
  add3DModel: (id, formData) => api.post(`/galleries/${id}/3d-model`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update3DSettings: (id, settings) => api.put(`/galleries/${id}/3d-model/settings`, settings),
  update: (id, data) => api.put(`/galleries/${id}`, data),
  delete: (id) => api.delete(`/galleries/${id}`),
  deleteFile: (id, filename) => api.delete(`/galleries/${id}/files/${filename}`),
  deleteVirtualTour: (id, tourId) => api.delete(`/galleries/${id}/virtual-tour/${tourId}`),
  send: (id) => api.post(`/galleries/${id}/send`),
  trackDownload: (shareLink) => api.post(`/gallery/${shareLink}/download`),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  uploadLogo: (formData) => api.post('/settings/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadBanner: (formData) => api.post('/settings/banner', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteLogo: () => api.delete('/settings/logo'),
  deleteBanner: () => api.delete('/settings/banner'),
};

// Pricing API
export const pricingAPI = {
  getAll: (params) => api.get('/pricing', { params }),
  getById: (id) => api.get(`/pricing/${id}`),
  getBySlug: (slug) => api.get(`/pricing/slug/${slug}`),
  create: (formData) => api.post('/pricing', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, formData) => api.put(`/pricing/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/pricing/${id}`),
  reorder: (packages) => api.post('/pricing/reorder', { packages }),
};

// Testimonials API
export const testimonialsAPI = {
  getAll: (params) => api.get('/testimonials', { params }),
  getAllAdmin: () => api.get('/testimonials/admin/all'),
  getById: (id) => api.get(`/testimonials/${id}`),
  create: (formData) => api.post('/testimonials', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, formData) => api.put(`/testimonials/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/testimonials/${id}`),
  deleteImage: (id, filename) => api.delete(`/testimonials/${id}/images/${filename}`),
  getRatingStats: () => api.get('/testimonials/stats/rating'),
  reorder: (testimonials) => api.post('/testimonials/reorder', { testimonials }),
};

// Automated Booking API
export const bookingAPI = {
  getAvailability: (date, opts = {}) => api.get('/booking/availability', {
    params: {
      date,
      ...(opts.package ? { package: opts.package } : {}),
      ...(opts.squareFootage ? { squareFootage: opts.squareFootage } : {}),
      ...(opts.acreage ? { acreage: opts.acreage } : {}),
      ...(typeof opts.addOnsCount === 'number' ? { addOnsCount: opts.addOnsCount } : {})
    }
  }),
  completeBooking: (bookingData) => api.post('/booking/complete-booking', bookingData),
  confirmDeposit: (invoiceId, paymentIntentId) => 
    api.post(`/booking/confirm-deposit/${invoiceId}`, { paymentIntentId }),
};

export const travelAPI = {
  // Preview only — the booking route recomputes this server-side.
  quote: (parts) => api.post('/travel/quote', parts),
};

// Discount Codes API
export const discountCodesAPI = {
  validate: (code, subtotal, packageId, category) => 
    api.post('/discount-codes/validate', { code, subtotal, packageId, category }),
  apply: (code) => api.post(`/discount-codes/apply/${code}`),
  getAll: (params) => api.get('/discount-codes', { params }),
  getById: (id) => api.get(`/discount-codes/${id}`),
  create: (data) => api.post('/discount-codes', data),
  update: (id, data) => api.put(`/discount-codes/${id}`, data),
  delete: (id) => api.delete(`/discount-codes/${id}`),
  getStats: (id) => api.get(`/discount-codes/${id}/stats`),
};

export default api;
